import { useState, useEffect, useCallback } from "react";
import { t } from "@/lib/i18n";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UsersThree, PaperPlaneTilt, Trash, Copy, Crown, PencilSimple, Check, LinkSimple } from "@phosphor-icons/react";
import { PageShell } from "@/components/PageShell";
import { SharedInboxesCard } from "@/components/SharedInboxesCard";
import api, { errDetail } from "@/lib/api";

export default function TeamPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [lastInviteLink, setLastInviteLink] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState("");

  const load = useCallback(() => api.get("/team").then((r) => setData(r.data)).catch((e) => toast.error(errDetail(e))), []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <PageShell title={t("team.title", "Team")} testId="team-loading"><p className="text-sm text-gray-400">{t("team.loading", "Loading…")}</p></PageShell>;

  if (!data.team) {
    return (
      <PageShell title={t("team.title", "Team")} subtitle={t("team.empty_subtitle", "Buy seats for your whole team — like Google Workspace.")} testId="team-empty-page">
        <div className="border border-dashed border-gray-300 rounded-md p-12 text-center">
          <UsersThree size={36} className="mx-auto mb-3 text-gray-300" />
          <p className="text-sm text-gray-600 mb-1 font-medium">{t("team.none_yet", "You don't have a team yet")}</p>
          <p className="text-sm text-gray-400 mb-6">{t("team.none_desc", "Purchase any plan — you'll become the team admin and can invite members up to your seat limit.")}</p>
          <button data-testid="team-goto-pricing-btn" onClick={() => navigate("/")}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-6 py-2.5 transition-colors">{t("team.view_plans", "View plans")}</button>
        </div>
      </PageShell>
    );
  }

  const { team, members, pending_invites, seats_used, seats_total, is_team_admin } = data;
  const pct = Math.min(100, Math.round((seats_used / seats_total) * 100));

  const invite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const { data: res } = await api.post("/team/invite", { email: inviteEmail, origin_url: window.location.origin });
      setLastInviteLink(res.invite_link);
      toast.success(res.sent_via_email ? t("team.invite_emailed", "Invitation emailed!") : t("team.invite_created", "Invitation created — share the link below (demo mode)"));
      setInviteEmail(""); load();
    } catch (err) { toast.error(errDetail(err)); }
    setInviting(false);
  };

  const revoke = async (inv) => {
    try { await api.delete(`/team/invites/${inv.id}`); toast.success(t("team.invite_revoked", "Invite revoked")); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const removeMember = async (m) => {
    try { await api.delete(`/team/members/${m.id}`); toast.success(t("team.removed", "{name} removed", { name: m.name })); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const rename = async () => {
    try { await api.put("/team", { name: newName }); toast.success(t("team.renamed", "Team renamed")); setRenaming(false); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <PageShell title={t("team.title", "Team")} subtitle={t("team.manage_subtitle", "{plan} plan · manage your seats and members.", { plan: team.package_name })} testId="team-page">
      <div className="border border-gray-200 rounded-md p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          {renaming ? (
            <div className="flex items-center gap-2">
              <input data-testid="team-rename-input" value={newName} onChange={(e) => setNewName(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <button data-testid="team-rename-save" onClick={rename} className="text-blue-600 hover:text-blue-800 p-1"><Check size={17} /></button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <h2 data-testid="team-name" className="font-heading font-bold text-lg text-gray-900">{team.name}</h2>
              {is_team_admin && (
                <button data-testid="team-rename-btn" onClick={() => { setRenaming(true); setNewName(team.name); }}
                  className="text-gray-400 hover:text-gray-700 p-1"><PencilSimple size={14} /></button>
              )}
            </div>
          )}
          <span data-testid="team-seat-count" className="text-sm font-medium text-gray-700">{t("team.seats_used", "{used} of {total} seats used", { used: seats_used, total: seats_total })}</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div data-testid="team-seat-bar" className={`h-full rounded-full transition-transform ${pct >= 100 ? "bg-amber-500" : "bg-blue-600"}`} style={{ width: `${pct}%` }} />
        </div>
        {pct >= 100 && <p className="text-xs text-amber-700 mt-2">{t("team.all_seats", "All seats used — upgrade your plan to invite more people.")}</p>}
      </div>

      {is_team_admin && (
        <div className="border border-gray-200 rounded-md p-5 mb-6">
          <h3 className="font-heading font-bold text-gray-900 mb-1">{t("team.invite_title", "Invite a teammate")}</h3>
          <p className="text-xs text-gray-500 mb-3">{t("team.invite_desc", "They'll get an invite link — emailed automatically via Brevo when configured, and always shown here to copy.")}</p>
          <form onSubmit={invite} className="flex gap-2">
            <input data-testid="invite-email-input" type="email" required value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="teammate@company.com"
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <button data-testid="invite-send-btn" disabled={inviting}
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-4 transition-colors disabled:opacity-50">
              <PaperPlaneTilt size={14} /> {t("team.invite", "Invite")}
            </button>
          </form>
          {lastInviteLink && (
            <div data-testid="invite-link-box" className="mt-3 flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
              <LinkSimple size={14} className="text-blue-600 shrink-0" />
              <code data-testid="invite-link-text" className="text-xs text-blue-900 break-all flex-1">{lastInviteLink}</code>
              <button data-testid="invite-link-copy" onClick={() => { navigator.clipboard?.writeText(lastInviteLink); toast.success(t("common.link_copied", "Link copied")); }}
                className="text-blue-600 hover:text-blue-800 p-1"><Copy size={14} /></button>
            </div>
          )}
        </div>
      )}

      <h3 className="font-heading font-bold text-gray-900 mb-3">{t("team.members", "Members")} ({members.length})</h3>
      <div className="space-y-2 mb-8">
        {members.map((m) => (
          <div key={m.id} data-testid={`member-row-${m.id}`} className="border border-gray-200 rounded-md p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold shrink-0">{(m.name || m.email || "?")[0]}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                {m.name}
                {m.id === team.owner_id && <Crown size={13} weight="fill" className="text-amber-500" title={t("team.owner", "Team owner")} />}
              </div>
              <div className="text-xs text-gray-500 truncate">{m.email}</div>
            </div>
            <span className={`text-xs rounded-full px-2.5 py-0.5 font-medium ${m.team_role === "team_admin" ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-600"}`}>
              {m.team_role === "team_admin" ? t("team.admin", "Team admin") : t("team.member", "Member")}
            </span>
            {is_team_admin && m.id !== team.owner_id && (
              <button data-testid={`member-remove-${m.id}`} onClick={() => removeMember(m)}
                className="text-gray-400 hover:text-red-600 transition-colors p-1"><Trash size={15} /></button>
            )}
          </div>
        ))}
      </div>

      {pending_invites.length > 0 && (
        <>
          <h3 className="font-heading font-bold text-gray-900 mb-3">{t("team.pending", "Pending invites")} ({pending_invites.length})</h3>
          <div className="space-y-2">
            {pending_invites.map((inv) => (
              <div key={inv.id} data-testid={`invite-row-${inv.id}`} className="border border-dashed border-gray-300 rounded-md p-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-800">{inv.email}</div>
                  <div className="text-xs text-gray-400">{t("team.invited_by", "Invited by {name}", { name: inv.invited_by })}</div>
                </div>
                <button data-testid={`invite-copy-${inv.id}`}
                  onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/join/${inv.token}`); toast.success(t("common.link_copied", "Link copied")); }}
                  className="text-gray-400 hover:text-blue-600 p-1"><Copy size={15} /></button>
                {is_team_admin && (
                  <button data-testid={`invite-revoke-${inv.id}`} onClick={() => revoke(inv)}
                    className="text-gray-400 hover:text-red-600 p-1"><Trash size={15} /></button>
                )}
              </div>
            ))}
          </div>
        </>
      )}
      <SharedInboxesCard members={members} />
    </PageShell>
  );
}
